import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Geometry is the app's own (38px min height, 9/13px padding, 17px icons)
// rather than shadcn's h-8/text-sm defaults, so the rendered result matches
// what .button produced. Font size is deliberately unset: Preflight gives
// buttons `font: inherit`, which is what the old stylesheet relied on.
const buttonVariants = cva(
  "inline-flex min-h-9.5 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg border border-transparent px-3.25 py-2.25 transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/15 disabled:cursor-not-allowed disabled:opacity-55 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4.25",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        outline: "border-border bg-transparent text-foreground hover:bg-muted",
        ghost: "bg-transparent text-foreground hover:bg-muted",
      },
      size: {
        default: "",
        // The step between `default` (38px) and an icon button. Unlike the
        // base, this one DOES set a font size: at 32px tall the inherited
        // body size fills the box edge to edge and the label reads as
        // cramped. Text has to come down with the geometry.
        sm: "min-h-8 gap-1.5 px-2.5 py-1.5 text-[13px] [&_svg:not([class*='size-'])]:size-3.75",
        icon: "w-9.5 px-0",
        // min-h-0 so the square size wins over the base min-height.
        "icon-sm": "size-7 min-h-0 px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
